<template>
  <!-- Loading Overlay - Mobile Optimized  -->
  <AppLoader
    v-if="isInitialLoading"
    :isVisible="isInitialLoading"
    variant="fullscreen"
    :message="loadingMessage || 'Loading Attendance Dashboard...'"
    subtext="Please wait while we prepare your attendance records and analytics..."
    color="indigo"
    icon="cap"
  />

  <!-- Main Content -->
  <div
    v-else
    class="space-y-4 sm:space-y-6"
  >
    <!-- Section Sub-Header & Export Action -->
    <div
      class="bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-md rounded-2xl p-3.5 sm:p-5 border border-slate-200/90 dark:border-[#263449] shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3"
    >
      <div class="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1">
        <div
          class="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0"
        >
          <History class="w-5 h-5" />
        </div>
        <div class="min-w-0 flex-1">
          <h2
            class="text-sm sm:text-base font-black text-slate-900 dark:text-[#F1F5F9] tracking-tight truncate"
          >
            Attendance Logs & Audit Trail
          </h2>
          <p class="text-xs text-slate-500 dark:text-[#A6B4C8] truncate sm:whitespace-normal">
            Multi-day logs, historical student rosters, and downloadable exports
          </p>
        </div>
      </div>

      <div class="w-full sm:w-auto pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100 dark:border-[#263449]/60 flex items-center justify-end sm:justify-start shrink-0">
        <button
          type="button"
          @click="exportToCSV"
          :disabled="
            isLoading ||
            isTableLoading ||
            isDateRangeLoading ||
            !history ||
            history.length === 0
          "
          class="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-3.5 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-all shadow-xs disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
          title="Export to CSV"
        >
          <Download class="w-3.5 h-3.5" />
          <span>Export CSV</span>
        </button>
      </div>
    </div>

    <!-- Filters Section -->
    <div
      class="bg-white dark:bg-[#111C30] rounded-xl sm:rounded-2xl border border-slate-200/90 dark:border-[#263449] p-3.5 sm:p-4 shadow-xs space-y-3.5"
    >
      <!-- Header with Title + Toggle Button (mobile only) -->
      <div class="flex items-center justify-between gap-3">
        <div class="flex items-center gap-2 min-w-0 flex-1">
          <div
            class="w-8 h-8 bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 rounded-lg flex items-center justify-center shrink-0"
          >
            <SlidersHorizontal class="w-4 h-4" />
          </div>
          <div class="min-w-0 flex-1">
            <h2 class="text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-100 truncate">
              Filter Attendance Records
            </h2>
            <p class="text-[11px] text-slate-400 dark:text-slate-500 truncate sm:whitespace-normal">
              Search students, select classroom, or specify date windows
            </p>
          </div>
        </div>

        <!-- Toggle button for mobile -->
        <button
          type="button"
          @click="showMobileFilters = !showMobileFilters"
          class="w-8 h-8 sm:hidden inline-flex items-center justify-center rounded-xl border border-slate-200 dark:border-[#263449] text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[#18243A] transition-colors shrink-0"
          :class="{ 'bg-slate-100 dark:bg-[#18243A]': showMobileFilters }"
          aria-label="Toggle filters"
        >
          <Filter class="w-4 h-4 shrink-0" />
        </button>
      </div>

      <!-- Filter Inputs -->
      <div
        :class="[
          'transition-all duration-300',
          showMobileFilters ? 'block' : 'hidden sm:block',
        ]"
      >
        <div
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-2.5 sm:gap-3 items-end"
        >
          <!-- Search Input -->
          <div
            :class="dateRangeOption === 'custom' ? 'lg:col-span-3 sm:col-span-2' : 'lg:col-span-4 sm:col-span-2'"
            class="space-y-1.5"
          >
            <label class="text-[11px] font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider block">Search</label>
            <div class="relative group">
              <Search
                class="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-[#7E8DA5] w-4 h-4 group-focus-within:text-blue-500 transition-colors pointer-events-none"
              />
              <input
                ref="searchInput"
                v-model="search"
                type="text"
                placeholder="Search by student name..."
                :disabled="isLoading || isTableLoading"
                class="w-full pl-10 pr-3.5 py-2.5 min-h-[42px] text-xs sm:text-sm bg-slate-50/80 dark:bg-[#18243A] text-slate-900 dark:text-[#F1F5F9] placeholder-slate-400 border border-slate-200/80 dark:border-[#263449] rounded-xl focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all font-semibold disabled:opacity-50"
              />
            </div>
          </div>

          <!-- Class Filter -->
          <div
            :class="dateRangeOption === 'custom' ? 'lg:col-span-2 sm:col-span-1' : 'lg:col-span-3 sm:col-span-1'"
            class="space-y-1.5"
          >
            <label class="text-[11px] font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider block">Class</label>
            <div class="relative group">
              <Users
                class="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-[#7E8DA5] w-4 h-4 group-focus-within:text-blue-500 transition-colors pointer-events-none"
              />
              <select
                v-model="selectedClass"
                :disabled="isLoading || isTableLoading"
                class="w-full pl-10 pr-9 py-2.5 min-h-[42px] text-xs sm:text-sm bg-slate-50/80 dark:bg-[#18243A] text-slate-900 dark:text-[#F1F5F9] border border-slate-200/80 dark:border-[#263449] rounded-xl focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all appearance-none cursor-pointer disabled:opacity-50 font-semibold"
              >
                <option value="">All Classes</option>
                <option
                  v-for="cls in availableClasses.filter(
                    (c) => c.name.toLowerCase() !== 'passout',
                  )"
                  :key="cls._id"
                  :value="cls._id"
                  :title="`${cls.name}${
                    cls.section ? ` - Section ${cls.section}` : ''
                  }`"
                >
                  {{ cls.name }}{{ cls.section ? ` - ${cls.section}` : "" }}
                </option>
              </select>
              <ChevronDown
                class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-[#7E8DA5] w-4 h-4 pointer-events-none"
              />
            </div>
          </div>

          <!-- Date Range Selector -->
          <div
            :class="dateRangeOption === 'custom' ? 'lg:col-span-2 sm:col-span-1' : 'lg:col-span-3 sm:col-span-1'"
            class="space-y-1.5"
          >
            <label class="text-[11px] font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider block">Range</label>
            <div class="relative group">
              <Calendar
                class="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-[#7E8DA5] w-4 h-4 group-focus-within:text-blue-500 transition-colors pointer-events-none"
              />
              <select
                v-model="dateRangeOption"
                :disabled="isLoading || isTableLoading"
                @change="handleDateRangeChange"
                class="w-full pl-10 pr-9 py-2.5 min-h-[42px] text-xs sm:text-sm bg-slate-50/80 dark:bg-[#18243A] text-slate-900 dark:text-[#F1F5F9] border border-slate-200/80 dark:border-[#263449] rounded-xl focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all appearance-none cursor-pointer disabled:opacity-50 font-semibold"
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last7days">Last 7 Days</option>
                <option value="last30days">Last 30 Days</option>
                <option value="custom">Custom Range</option>
              </select>
              <ChevronDown
                class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-[#7E8DA5] w-4 h-4 pointer-events-none"
              />
            </div>
          </div>

          <!-- Custom Date Range Inputs (when custom selected) -->
          <template v-if="dateRangeOption === 'custom'">
            <div class="lg:col-span-2 col-span-1 space-y-1.5">
              <label class="text-[11px] font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider block">Start Date</label>
              <div class="relative group">
                <Calendar
                  class="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-[#7E8DA5] w-4 h-4 group-focus-within:text-blue-500 transition-colors pointer-events-none"
                />
                <input
                  v-model="startDate"
                  type="date"
                  :disabled="isLoading || isTableLoading"
                  class="w-full pl-10 pr-3 py-2.5 min-h-[42px] text-xs sm:text-sm bg-slate-50/80 dark:bg-[#18243A] text-slate-900 dark:text-[#F1F5F9] border border-slate-200/80 dark:border-[#263449] rounded-xl focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all font-semibold"
                />
              </div>
            </div>
            <div class="lg:col-span-2 col-span-1 space-y-1.5">
              <label class="text-[11px] font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider block">End Date</label>
              <div class="relative group">
                <Calendar
                  class="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-[#7E8DA5] w-4 h-4 group-focus-within:text-blue-500 transition-colors pointer-events-none"
                />
                <input
                  v-model="endDate"
                  type="date"
                  :disabled="isLoading || isTableLoading"
                  class="w-full pl-10 pr-3 py-2.5 min-h-[42px] text-xs sm:text-sm bg-slate-50/80 dark:bg-[#18243A] text-slate-900 dark:text-[#F1F5F9] border border-slate-200/80 dark:border-[#263449] rounded-xl focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all font-semibold"
                />
              </div>
            </div>
          </template>

          <!-- Buttons: All Records / Mine Only & Refresh -->
          <div
            :class="dateRangeOption === 'custom' ? 'lg:col-span-1 sm:col-span-2' : 'lg:col-span-2 sm:col-span-2'"
            class="flex items-center gap-2 pt-1 sm:pt-0 self-end w-full"
          >
            <button
              :class="[
                'px-3 py-2.5 min-h-[42px] rounded-xl text-xs font-bold transition-all duration-200 border flex-1 flex items-center justify-center gap-1.5 shadow-xs cursor-pointer',
                isMineOnly
                  ? 'bg-blue-600 text-white border-blue-600 shadow-blue-500/20'
                  : 'bg-slate-50 dark:bg-[#18243A] text-slate-700 dark:text-[#A6B4C8] border-slate-200/80 dark:border-[#263449] hover:bg-slate-100 dark:hover:bg-[#172235]',
                isLoading || isTableLoading ? 'opacity-50 cursor-not-allowed' : '',
              ]"
              @click="
                isMineOnly = !isMineOnly;
                fetchFilteredHistory();
              "
              :disabled="isLoading || isTableLoading"
              :title="isMineOnly ? 'Showing records created by you' : 'Showing all records'"
            >
              <span>{{ isMineOnly ? "Mine" : "All" }}</span>
            </button>

            <button
              class="px-3.5 py-2.5 min-h-[42px] rounded-xl text-xs font-bold transition-all duration-200 bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/40 hover:bg-blue-100 dark:hover:bg-blue-900/40 disabled:opacity-50 cursor-pointer flex items-center justify-center shadow-xs"
              @click="fetchFilteredHistory()"
              :disabled="isLoading || isTableLoading"
              title="Refresh Records"
            >
              <RefreshCw
                :class="[
                  'w-4 h-4',
                  isLoading || isTableLoading ? 'animate-spin' : '',
                ]"
              />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Stats Cards (Matching Dashboard Stats Card design system) -->
    <div
      v-if="dateRangeOption === 'today' && !isDateRangeLoading"
      class="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3.5 lg:gap-4 mb-3 sm:mb-4"
    >
      <!-- Present Students -->
      <div
        class="bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-2xl p-3.5 sm:p-4 relative shadow-xs hover:shadow-md hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-300 flex flex-col justify-between overflow-hidden group"
      >
        <div
          v-if="isStatsLoading"
          class="absolute inset-0 bg-white/80 dark:bg-[#111C30]/80 backdrop-blur-xs flex items-center justify-center rounded-2xl z-10"
        >
          <AppLoader variant="inline" size="xs" color="emerald" />
        </div>
        <div>
          <div class="flex items-center justify-between mb-2">
            <div class="w-8 h-8 sm:w-9 sm:h-9 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200/60 dark:border-emerald-800/40 rounded-xl text-emerald-600 dark:text-emerald-400 flex items-center justify-center shadow-xs group-hover:scale-105 transition-transform">
              <UserCheck class="w-4 h-4 sm:w-4.5 sm:h-4.5" />
            </div>
            <span
              class="text-[10px] font-bold bg-slate-100 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] px-2 py-0.5 rounded-md border border-slate-200/60 dark:border-[#263449]"
            >
              {{ displayDate }}
            </span>
          </div>
          <h3 class="text-xs font-bold text-slate-600 dark:text-[#A6B4C8]">
            Present Students
          </h3>
          <p class="text-2xl sm:text-3xl font-black text-slate-900 dark:text-[#F1F5F9] mt-0.5 tracking-tight">
            <template v-if="!isStatsLoading">
              {{
                selectedClass
                  ? stats.today?.classWise?.[String(selectedClass)]?.present || 0
                  : stats.today?.overall?.totalPresent || 0
              }}
            </template>
            <template v-else>--</template>
          </p>
        </div>
        <div class="mt-2.5">
          <div class="w-full bg-slate-100 dark:bg-[#18243A] h-2 rounded-full overflow-hidden">
            <div
              class="bg-emerald-500 h-full rounded-full transition-all duration-500"
              :style="{
                width: `${
                  (selectedClass
                    ? (stats.today?.classWise?.[String(selectedClass)]?.totalStudents ? ((stats.today?.classWise?.[String(selectedClass)]?.present || 0) / stats.today?.classWise?.[String(selectedClass)]?.totalStudents) * 100 : 0)
                    : (stats.today?.overall?.totalStudents ? ((stats.today?.overall?.totalPresent || 0) / stats.today?.overall?.totalStudents) * 100 : 0))
                }%`,
              }"
            ></div>
          </div>
          <p class="text-slate-500 dark:text-[#7E8DA5] text-[11px] font-medium mt-1.5 flex items-center justify-between">
            <span>Out of
              <strong class="text-slate-700 dark:text-slate-300">
                <template v-if="!isStatsLoading">
                  {{
                    selectedClass
                      ? stats.today?.classWise?.[String(selectedClass)]?.totalStudents || 0
                      : stats.today?.overall?.totalStudents || 0
                  }}
                </template>
                <template v-else>--</template>
              </strong> students
            </span>
          </p>
        </div>
      </div>

      <!-- Absent Students -->
      <div
        class="bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-2xl p-3.5 sm:p-4 relative shadow-xs hover:shadow-md hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-300 flex flex-col justify-between overflow-hidden group"
      >
        <div
          v-if="isStatsLoading"
          class="absolute inset-0 bg-white/80 dark:bg-[#111C30]/80 backdrop-blur-xs flex items-center justify-center rounded-2xl z-10"
        >
          <AppLoader variant="inline" size="xs" color="rose" />
        </div>
        <div>
          <div class="flex items-center justify-between mb-2">
            <div class="w-8 h-8 sm:w-9 sm:h-9 bg-rose-50 dark:bg-rose-950/40 border border-rose-200/60 dark:border-rose-800/40 rounded-xl text-rose-600 dark:text-rose-400 flex items-center justify-center shadow-xs group-hover:scale-105 transition-transform">
              <UserX class="w-4 h-4 sm:w-4.5 sm:h-4.5" />
            </div>
            <span
              class="text-[10px] font-bold bg-slate-100 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] px-2 py-0.5 rounded-md border border-slate-200/60 dark:border-[#263449]"
            >
              {{ displayDate }}
            </span>
          </div>
          <h3 class="text-xs font-bold text-slate-600 dark:text-[#A6B4C8]">
            Absent Students
          </h3>
          <p class="text-2xl sm:text-3xl font-black text-slate-900 dark:text-[#F1F5F9] mt-0.5 tracking-tight">
            <template v-if="!isStatsLoading">
              {{
                selectedClass
                  ? stats.today?.classWise?.[String(selectedClass)]?.absent || 0
                  : stats.today?.overall?.totalAbsent || 0
              }}
            </template>
            <template v-else>--</template>
          </p>
        </div>
        <div class="mt-2.5">
          <div class="w-full bg-slate-100 dark:bg-[#18243A] h-2 rounded-full overflow-hidden">
            <div
              class="bg-rose-500 h-full rounded-full transition-all duration-500"
              :style="{ width: `${100 - getAttendancePercentage()}%` }"
            ></div>
          </div>
          <p class="text-slate-500 dark:text-[#7E8DA5] text-[11px] font-medium mt-1.5 flex items-center justify-between">
            <span>
              <strong class="text-slate-700 dark:text-slate-300">
                <template v-if="!isStatsLoading">{{ getAttendancePercentage() }}%</template>
                <template v-else>--%</template>
              </strong> attendance rate
            </span>
          </p>
        </div>
      </div>

      <!-- Late Students -->
      <div
        class="bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-2xl p-3.5 sm:p-4 relative shadow-xs hover:shadow-md hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-300 flex flex-col justify-between overflow-hidden group"
      >
        <div
          v-if="isStatsLoading"
          class="absolute inset-0 bg-white/80 dark:bg-[#111C30]/80 backdrop-blur-xs flex items-center justify-center rounded-2xl z-10"
        >
          <AppLoader variant="inline" size="xs" color="amber" />
        </div>
        <div>
          <div class="flex items-center justify-between mb-2">
            <div class="w-8 h-8 sm:w-9 sm:h-9 bg-amber-50 dark:bg-amber-950/40 border border-amber-200/60 dark:border-amber-800/40 rounded-xl text-amber-600 dark:text-amber-400 flex items-center justify-center shadow-xs group-hover:scale-105 transition-transform">
              <Clock class="w-4 h-4 sm:w-4.5 sm:h-4.5" />
            </div>
            <span
              class="text-[10px] font-bold bg-slate-100 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] px-2 py-0.5 rounded-md border border-slate-200/60 dark:border-[#263449]"
            >
              {{ displayDate }}
            </span>
          </div>
          <h3 class="text-xs font-bold text-slate-600 dark:text-[#A6B4C8]">
            Late Arrivals
          </h3>
          <p class="text-2xl sm:text-3xl font-black text-slate-900 dark:text-[#F1F5F9] mt-0.5 tracking-tight">
            <template v-if="!isStatsLoading">
              {{
                selectedClass
                  ? stats.today?.classWise?.[String(selectedClass)]?.late || 0
                  : stats.today?.overall?.totalLate || 0
              }}
            </template>
            <template v-else>--</template>
          </p>
        </div>
        <div class="mt-2.5">
          <div class="w-full bg-slate-100 dark:bg-[#18243A] h-2 rounded-full overflow-hidden">
            <div
              class="bg-amber-500 h-full rounded-full transition-all duration-500"
              :style="{
                width: `${
                  (selectedClass
                    ? (stats.today?.classWise?.[String(selectedClass)]?.totalStudents ? ((stats.today?.classWise?.[String(selectedClass)]?.late || 0) / stats.today?.classWise?.[String(selectedClass)]?.totalStudents) * 100 : 0)
                    : (stats.today?.overall?.totalStudents ? ((stats.today?.overall?.totalLate || 0) / stats.today?.overall?.totalStudents) * 100 : 0))
                }%`,
              }"
            ></div>
          </div>
          <p class="text-slate-500 dark:text-[#7E8DA5] text-[11px] font-medium mt-1.5 flex items-center justify-between">
            <span>Late recorded entries</span>
          </p>
        </div>
      </div>

      <!-- Average Attendance -->
      <div
        class="bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-2xl p-3.5 sm:p-4 relative shadow-xs hover:shadow-md hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-300 flex flex-col justify-between overflow-hidden group"
      >
        <div
          v-if="isStatsLoading"
          class="absolute inset-0 bg-white/80 dark:bg-[#111C30]/80 backdrop-blur-xs flex items-center justify-center rounded-2xl z-10"
        >
          <AppLoader variant="inline" size="xs" color="indigo" />
        </div>
        <div>
          <div class="flex items-center justify-between mb-2">
            <div class="w-8 h-8 sm:w-9 sm:h-9 bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200/60 dark:border-indigo-800/40 rounded-xl text-indigo-600 dark:text-indigo-400 flex items-center justify-center shadow-xs group-hover:scale-105 transition-transform">
              <TrendingUp class="w-4 h-4 sm:w-4.5 sm:h-4.5" />
            </div>
            <span
              class="text-[10px] font-bold bg-slate-100 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] px-2 py-0.5 rounded-md border border-slate-200/60 dark:border-[#263449]"
            >
              7 Days
            </span>
          </div>
          <h3 class="text-xs font-bold text-slate-600 dark:text-[#A6B4C8]">
            Avg Attendance
          </h3>
          <p class="text-2xl sm:text-3xl font-black text-slate-900 dark:text-[#F1F5F9] mt-0.5 tracking-tight">
            <template v-if="!isStatsLoading">{{ getWeeklyAverage() }}%</template>
            <template v-else>--%</template>
          </p>
        </div>
        <div class="mt-2.5">
          <div class="w-full bg-slate-100 dark:bg-[#18243A] h-2 rounded-full overflow-hidden">
            <div
              class="bg-indigo-500 h-full rounded-full transition-all duration-500"
              :style="{ width: `${getWeeklyAverage()}%` }"
            ></div>
          </div>
          <p class="text-slate-500 dark:text-[#7E8DA5] text-[11px] font-medium mt-1.5 flex items-center justify-between">
            <span>Past week average rate</span>
          </p>
        </div>
      </div>
    </div>

    <!-- Date Range Stats -->
    <div
      v-if="dateRangeOption !== 'today' && !isDateRangeLoading"
      class="bg-white dark:bg-[#111C30] rounded-2xl shadow-xs border border-slate-200/90 dark:border-[#263449] p-3.5 sm:p-5 mb-3 sm:mb-4 relative overflow-hidden"
    >
      <div class="flex items-center justify-between mb-3.5">
        <div class="flex items-center gap-2">
          <div class="w-2.5 h-2.5 rounded-full bg-blue-500 shadow-xs shadow-blue-500/50 animate-pulse"></div>
          <h3 class="text-xs sm:text-sm font-black text-slate-900 dark:text-[#F1F5F9] tracking-tight">
            Date Range Summary
          </h3>
        </div>
        <div class="text-xs font-bold text-slate-600 dark:text-[#A6B4C8] bg-slate-100 dark:bg-[#18243A] px-3 py-1 rounded-xl border border-slate-200 dark:border-[#263449]">
          {{ formatDateRange(startDate, endDate) }}
        </div>
      </div>

      <div class="grid grid-cols-3 gap-2.5 sm:gap-3.5">
        <div class="text-center p-3 sm:p-4 bg-blue-50/60 dark:bg-blue-950/30 rounded-2xl border border-blue-100 dark:border-blue-900/40 shadow-xs">
          <div class="text-xl sm:text-2xl font-black text-blue-600 dark:text-blue-400 tracking-tight">
            {{ totalResults }}
          </div>
          <div class="text-[11px] sm:text-xs font-bold text-slate-600 dark:text-[#A6B4C8] mt-0.5">Total Records</div>
        </div>

        <div class="text-center p-3 sm:p-4 bg-emerald-50/60 dark:bg-emerald-950/30 rounded-2xl border border-emerald-100 dark:border-emerald-900/40 shadow-xs">
          <div class="text-xl sm:text-2xl font-black text-emerald-600 dark:text-emerald-400 tracking-tight">
            {{ presentCount }}
          </div>
          <div class="text-[11px] sm:text-xs font-bold text-slate-600 dark:text-[#A6B4C8] mt-0.5">Present</div>
        </div>

        <div class="text-center p-3 sm:p-4 bg-rose-50/60 dark:bg-rose-950/30 rounded-2xl border border-rose-100 dark:border-rose-900/40 shadow-xs">
          <div class="text-xl sm:text-2xl font-black text-rose-600 dark:text-rose-400 tracking-tight">
            {{ absentCount }}
          </div>
          <div class="text-[11px] sm:text-xs font-bold text-slate-600 dark:text-[#A6B4C8] mt-0.5">Absent</div>
        </div>
      </div>
    </div>

    <!-- Attendance History Table & Cards -->
    <div
      class="bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-2xl overflow-hidden relative shadow-xs"
    >
      <!-- Table Loading Overlay -->
      <div
        v-if="isTableLoading || isDateRangeLoading"
        class="absolute inset-0 bg-white/90 dark:bg-[#111C30]/90 z-10 flex items-center justify-center backdrop-blur-sm"
      >
        <div class="text-center">
          <div class="flex items-center justify-center space-x-2 mb-3">
            <span
              class="w-2.5 h-2.5 bg-blue-600 rounded-full animate-bounce"
            ></span>
            <span
              class="w-2.5 h-2.5 bg-blue-400 rounded-full animate-bounce [animation-delay:-0.2s]"
            ></span>
            <span
              class="w-2.5 h-2.5 bg-blue-300 rounded-full animate-bounce [animation-delay:-0.4s]"
            ></span>
          </div>

          <p class="text-xs font-semibold text-slate-600 dark:text-[#A6B4C8]">
            {{
              isDateRangeLoading
                ? dateRangeLoadingMessage
                : tableLoadingMessage
            }}
          </p>
        </div>
      </div>

      <!-- Table Header Bar -->
      <div
        class="p-3.5 sm:p-5 border-b border-slate-200/80 dark:border-[#263449] flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-3"
      >
        <div class="min-w-0 flex-1">
          <h3 class="text-sm sm:text-base font-bold text-slate-900 dark:text-[#F1F5F9] truncate">
            Recent Attendance Records
          </h3>
          <p class="text-xs text-slate-500 dark:text-[#A6B4C8] truncate sm:whitespace-normal">
            Latest entries, verification logs, and classroom audit details
          </p>
        </div>
        <button
          type="button"
          @click="exportToCSV"
          :disabled="
            isLoading ||
            isTableLoading ||
            isDateRangeLoading ||
            !history ||
            history.length === 0
          "
          class="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-700 dark:text-[#F1F5F9] bg-slate-50 dark:bg-[#18243A] hover:bg-slate-100 dark:hover:bg-[#172235] border border-slate-200/90 dark:border-[#263449] rounded-xl transition-all shadow-xs disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
          title="Export to CSV"
        >
          <Download class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 shrink-0" />
          <span>Export CSV</span>
        </button>
      </div>

      <!-- Content Area -->
      <div v-if="!isTableLoading && !isDateRangeLoading">
        <!-- Mobile View - Cards for single date only -->
        <div
          class="block md:hidden p-3"
          v-if="dateRangeOption === 'today'"
        >
          <!-- Mobile Cards -->
          <div class="space-y-3">
            <div
              v-for="entry in sortedHistory"
              :key="entry._id"
              class="bg-white dark:bg-[#111C30] rounded-2xl p-4 border border-slate-200/90 dark:border-[#263449] shadow-xs relative overflow-hidden flex flex-col justify-between hover:shadow-md transition-all duration-200"
            >
              <!-- Top Bar: Date & Status Pill -->
              <div class="flex items-center justify-between mb-3">
                <div class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-[#18243A] text-slate-700 dark:text-slate-200 border border-slate-200/80 dark:border-[#263449]">
                  <Calendar class="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                  <span class="text-xs font-bold">
                    {{ formatDate(entry.date) }}
                  </span>
                </div>

                <div
                  :class="[
                    'inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold rounded-full border shadow-2xs',
                    entry.status === 'present'
                      ? 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border-emerald-200/80 dark:border-emerald-800/40'
                      : entry.status === 'absent'
                      ? 'bg-rose-50 dark:bg-rose-950/50 text-rose-700 dark:text-rose-300 border-rose-200/80 dark:border-rose-800/40'
                      : entry.status === 'late'
                      ? 'bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 border-amber-200/80 dark:border-amber-800/40'
                      : 'bg-slate-100 dark:bg-[#18243A] text-slate-700 dark:text-[#A6B4C8] border-slate-200 dark:border-[#263449]'
                  ]"
                >
                  <component
                    :is="entry.status === 'present' ? CheckCircle2 : entry.status === 'absent' ? XCircle : entry.status === 'late' ? Clock : AlertCircle"
                    class="w-3.5 h-3.5"
                  />
                  <span>{{ capitalizeStatus(entry.status) }}</span>
                </div>
              </div>

              <!-- Student Profile Row -->
              <div class="flex items-center gap-3 mb-3">
                <div class="relative shrink-0">
                  <div
                    class="w-11 h-11 min-w-[44px] max-w-[44px] min-h-[44px] max-h-[44px] rounded-xl overflow-hidden border border-slate-200/80 dark:border-[#263449] ring-2 ring-slate-100 dark:ring-[#1E293B] flex items-center justify-center bg-slate-100 dark:bg-[#18243A] shadow-2xs"
                  >
                    <img
                      v-if="entry.studentPhoto"
                      :src="entry.studentPhoto"
                      alt="Student Photo"
                      class="w-full h-full object-cover block"
                    />
                    <div
                      v-else
                      class="w-full h-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white font-black text-sm uppercase"
                    >
                      {{ getInitials(entry.studentName) }}
                    </div>
                  </div>
                  <!-- Mini Status Dot on Avatar -->
                  <div
                    class="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-[#111C30]"
                    :class="getStatusDotClass(entry.status)"
                  ></div>
                </div>

                <div class="flex-1 min-w-0">
                  <h4 class="text-sm font-extrabold text-slate-900 dark:text-white truncate">
                    {{ entry.studentName || "N/A" }}
                  </h4>
                  <div class="flex items-center gap-1.5 mt-1">
                    <span class="inline-flex items-center gap-1 font-mono text-[11px] font-bold text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-[#18243A] px-2 py-0.5 rounded-md border border-slate-200/80 dark:border-[#263449]">
                      <Hash class="w-3 h-3 text-slate-400" />
                      {{ entry.rollNumber || "N/A" }}
                    </span>
                    <span
                      v-if="entry.className || entry.class?.name"
                      class="text-[11px] font-bold text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/50 px-2 py-0.5 rounded-md border border-indigo-200/60 dark:border-indigo-800/40 truncate max-w-[120px]"
                    >
                      {{ entry.className || entry.class?.name }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Metadata Info Grid -->
              <div class="grid grid-cols-2 gap-2 pt-2.5 border-t border-slate-100 dark:border-[#263449]/70 bg-slate-50/70 dark:bg-[#18243A]/50 -mx-4 -mb-4 p-3 rounded-b-2xl text-xs">
                <div class="flex items-center gap-2 min-w-0">
                  <div class="w-7 h-7 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-900/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0">
                    <component :is="entry.device?.deviceName ? Laptop : User" class="w-3.5 h-3.5" />
                  </div>
                  <div class="min-w-0">
                    <div class="text-[10px] uppercase font-bold text-slate-400 dark:text-[#7E8DA5] tracking-wider">Logged By</div>
                    <div class="text-xs font-bold text-slate-800 dark:text-[#F1F5F9] truncate" :title="entry.teacherName || entry.device?.deviceName || 'N/A'">
                      {{ entry.teacherName || entry.device?.deviceName || "N/A" }}
                    </div>
                  </div>
                </div>

                <div class="flex items-center gap-2 min-w-0">
                  <div class="w-7 h-7 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-900/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0">
                    <Clock class="w-3.5 h-3.5" />
                  </div>
                  <div class="min-w-0">
                    <div class="text-[10px] uppercase font-bold text-slate-400 dark:text-[#7E8DA5] tracking-wider">Timestamp</div>
                    <div class="text-xs font-bold text-slate-800 dark:text-[#F1F5F9] truncate">
                      {{ formatTime(entry.updatedAt || entry.createdAt) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div
            v-if="!sortedHistory || sortedHistory.length === 0"
            class="text-center py-12"
          >
            <div
              class="w-12 h-12 bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-900/30 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-xs"
            >
              <ClipboardList class="w-6 h-6" />
            </div>
            <h3 class="text-sm font-bold text-slate-900 dark:text-[#F1F5F9] mb-1">
              No attendance records found
            </h3>
            <p class="text-xs text-slate-500 dark:text-[#A6B4C8]">
              Try adjusting your filters or check back later.
            </p>
          </div>
        </div>

        <!-- Mobile View - Table for date range only -->
          <div
            class="block md:hidden w-full overflow-hidden p-3"
            v-if="dateRangeOption !== 'today'"
          >
            <!-- Enhanced mobile table with sticky student identity & smooth scroll -->
            <div class="relative">
              <!-- Scrollable table container -->
              <div
                class="overflow-x-auto scrollbar-thin scrollbar-thumb-slate-300 dark:scrollbar-thumb-[#263449] scrollbar-track-slate-100 dark:scrollbar-track-[#0B1120] rounded-2xl border border-slate-200/80 dark:border-[#263449]"
              >
                <table class="w-full text-xs">
                  <!-- Date Range View Headers -->
                  <thead
                    class="bg-slate-50 dark:bg-[#0F172A] border-b border-slate-200/80 dark:border-[#263449] sticky top-0 z-20"
                  >
                    <tr>
                      <th
                        class="px-2.5 py-3 text-left text-[11px] font-black text-slate-600 dark:text-[#7E8DA5] uppercase tracking-wider min-w-[55px] sticky left-0 bg-slate-100 dark:bg-[#0B1120] z-30 shadow-xs"
                      >
                        Roll
                      </th>
                      <th
                        class="px-3 py-3 text-left text-[11px] font-black text-slate-600 dark:text-[#7E8DA5] uppercase tracking-wider min-w-[125px] sticky left-[55px] bg-slate-100 dark:bg-[#0B1120] z-30 border-r border-slate-200/80 dark:border-[#263449] shadow-xs"
                      >
                        Name
                      </th>
                      <th
                        v-for="date in dateRangeArray"
                        :key="date"
                        class="px-2 py-3 text-center text-[11px] font-black text-slate-600 dark:text-[#7E8DA5] uppercase tracking-wider min-w-[75px]"
                      >
                        <div class="flex flex-col">
                          <span class="text-[10px] font-bold">{{
                            formatDateHeader(date).split(" ")[0]
                          }}</span>
                          <span class="text-[9px] text-slate-400 dark:text-[#7E8DA5]">{{
                            formatDateHeader(date).split(" ")[1]
                          }}</span>
                        </div>
                      </th>
                      <th
                        class="px-2 py-3 text-center text-[11px] font-black text-slate-600 dark:text-[#7E8DA5] uppercase tracking-wider min-w-[75px]"
                      >
                        Status
                      </th>
                      <th
                        class="px-2.5 py-3 text-left text-[11px] font-black text-slate-600 dark:text-[#7E8DA5] uppercase tracking-wider min-w-[100px]"
                      >
                        Recorded By
                      </th>
                    </tr>
                  </thead>

                  <!-- Date Range View Body -->
                  <tbody class="bg-white dark:bg-[#111C30] divide-y divide-slate-200/70 dark:divide-[#263449]">
                    <tr
                      v-for="student in groupedRangeData"
                      :key="student.studentId"
                      class="hover:bg-slate-50/60 dark:hover:bg-[#18243A]/50 transition-colors"
                    >
                      <td class="px-2.5 py-3 whitespace-nowrap sticky left-0 bg-white dark:bg-[#111C30] z-20 shadow-xs">
                        <span class="font-mono text-[11px] font-bold text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-[#18243A] px-1.5 py-0.5 rounded border border-slate-200/60 dark:border-[#263449]">
                          {{ student.rollNo || "N/A" }}
                        </span>
                      </td>
                      <td class="px-3 py-3 whitespace-nowrap sticky left-[55px] bg-white dark:bg-[#111C30] z-20 border-r border-slate-200/80 dark:border-[#263449] shadow-xs">
                        <div class="flex items-center gap-2">
                          <img
                            v-if="student.studentPhoto"
                            :src="student.studentPhoto"
                            class="h-6 w-6 rounded-lg object-cover border border-slate-200/90 dark:border-[#263449] shrink-0"
                          />
                          <div
                            v-else
                            class="h-6 w-6 rounded-lg bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shrink-0 shadow-xs"
                          >
                            <span class="text-[9px] font-bold text-white">
                              {{ getInitials(student.studentName) }}
                            </span>
                          </div>

                          <span
                            class="text-xs font-bold text-slate-900 dark:text-[#F1F5F9] truncate max-w-[90px]"
                            :title="student.studentName"
                          >
                            {{ student.studentName }}
                          </span>
                        </div>
                      </td>
                      <td
                        v-for="date in dateRangeArray"
                        :key="date"
                        class="px-2 py-3 text-center whitespace-nowrap"
                      >
                        <span
                          v-if="student.attendance[date]"
                          :class="
                            getStatusClass(student.attendance[date].status)
                          "
                          class="inline-flex items-center justify-center w-6 h-6 text-[10px] font-bold rounded-lg border shadow-xs"
                        >
                          {{ getStatusIcon(student.attendance[date].status) }}
                        </span>
                        <span
                          v-else
                          class="text-slate-300 dark:text-slate-600 text-xs"
                          >-</span
                        >
                      </td>
                      <td class="px-2 py-3 whitespace-nowrap text-center">
                        <span
                          class="text-xs font-bold truncate max-w-[120px]"
                          :class="{
                            'text-emerald-600 dark:text-emerald-400':
                              student.overallStatus === 'Present',
                            'text-rose-600 dark:text-rose-400':
                              student.overallStatus === 'Absent',
                            'text-slate-700 dark:text-[#A6B4C8]':
                              student.overallStatus !== 'Present' &&
                              student.overallStatus !== 'Absent',
                          }"
                          v-html="student.overallStatus"
                          :title="student.overallStatus.replace(/<[^>]+>/g, '')"
                        >
                        </span>
                      </td>

                      <td class="px-2.5 py-3 whitespace-nowrap">
                        <span
                          class="text-xs font-medium text-slate-500 dark:text-[#A6B4C8] truncate max-w-[90px] block"
                          :title="
                            getStudentDeviceName(student) ||
                            student.lastTeacher ||
                            'N/A'
                          "
                        >
                          {{
                            getStudentDeviceName(student) ||
                            student.lastTeacher ||
                            "N/A"
                          }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Scroll hint for mobile users -->
              <div class="flex items-center justify-center gap-1.5 mt-2.5 text-[11px] font-semibold text-slate-400 dark:text-[#7E8DA5]">
                <span>← Scroll horizontally to view all dates →</span>
              </div>
            </div>

            <!-- Mobile Empty State for date range -->
            <div
              v-if="!groupedRangeData || groupedRangeData.length === 0"
              class="text-center py-12"
            >
              <div
                class="w-12 h-12 bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-900/30 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-xs"
              >
                <ClipboardList class="w-6 h-6" />
              </div>
              <h3 class="text-sm font-bold text-slate-900 dark:text-[#F1F5F9] mb-1">
                No attendance records found
              </h3>
              <p class="text-xs text-slate-500 dark:text-[#A6B4C8]">
                Try adjusting your filters or check back later.
              </p>
            </div>
          </div>

          <!-- Desktop View - Table -->
          <div class="hidden md:block overflow-x-auto">
            <!-- Actual Table -->
            <table class="w-full text-left border-collapse">
              <!-- Single Date View Headers -->
              <thead
                v-if="dateRangeOption === 'today'"
                class="bg-slate-50/80 dark:bg-[#0F172A] border-b border-slate-200/80 dark:border-[#263449]"
              >
                <tr>
                  <th
                    class="px-4 py-3.5 text-xs font-bold text-slate-600 dark:text-[#7E8DA5] uppercase tracking-wider"
                  >
                    Date
                  </th>
                  <th
                    class="px-4 py-3.5 text-xs font-bold text-slate-600 dark:text-[#7E8DA5] uppercase tracking-wider"
                  >
                    Roll
                  </th>
                  <th
                    class="px-4 py-3.5 text-xs font-bold text-slate-600 dark:text-[#7E8DA5] uppercase tracking-wider"
                  >
                    Student
                  </th>
                  <th
                    class="px-4 py-3.5 text-xs font-bold text-slate-600 dark:text-[#7E8DA5] uppercase tracking-wider"
                  >
                    Status
                  </th>
                  <th
                    class="px-4 py-3.5 text-xs font-bold text-slate-600 dark:text-[#7E8DA5] uppercase tracking-wider"
                  >
                    Recorded By / Device
                  </th>
                  <th
                    class="px-4 py-3.5 text-xs font-bold text-slate-600 dark:text-[#7E8DA5] uppercase tracking-wider"
                  >
                    Logged Time
                  </th>
                </tr>
              </thead>

              <!-- Date Range View Headers -->
              <thead
                v-else
                class="bg-slate-50/80 dark:bg-[#0F172A] border-b border-slate-200/80 dark:border-[#263449]"
              >
                <tr>
                  <th
                    class="px-4 py-3.5 text-xs font-bold text-slate-600 dark:text-[#7E8DA5] uppercase tracking-wider"
                  >
                    Roll No
                  </th>
                  <th
                    class="px-4 py-3.5 text-xs font-bold text-slate-600 dark:text-[#7E8DA5] uppercase tracking-wider"
                  >
                    Student Name
                  </th>
                  <th
                    v-for="date in dateRangeArray"
                    :key="date"
                    class="px-2 py-3.5 text-center text-xs font-bold text-slate-600 dark:text-[#7E8DA5] uppercase tracking-wider min-w-[90px]"
                  >
                    <div class="flex flex-col">
                      <span class="text-[11px] font-bold">{{ formatDateHeader(date).split(" ")[0] }}</span>
                      <span class="text-[10px] text-slate-400 dark:text-[#7E8DA5] font-medium">{{ formatDateHeader(date).split(" ")[1] }}</span>
                    </div>
                  </th>
                  <th
                    class="px-4 py-3.5 text-center text-xs font-bold text-slate-600 dark:text-[#7E8DA5] uppercase tracking-wider"
                  >
                    Summary Status
                  </th>
                  <th
                    class="px-4 py-3.5 text-xs font-bold text-slate-600 dark:text-[#7E8DA5] uppercase tracking-wider"
                  >
                    Recorded By / Device
                  </th>
                </tr>
              </thead>

              <!-- Single Date View Body -->
              <tbody
                v-if="dateRangeOption === 'today'"
                class="divide-y divide-slate-200/70 dark:divide-[#263449] bg-white dark:bg-[#111C30]"
              >
                <tr
                  v-for="entry in sortedHistory"
                  :key="entry._id"
                  class="hover:bg-slate-50/60 dark:hover:bg-[#18243A]/50 transition-colors"
                >
                  <td class="px-4 py-3.5 whitespace-nowrap">
                    <div class="flex items-center gap-2">
                      <Calendar class="w-3.5 h-3.5 text-slate-400 dark:text-[#7E8DA5]" />
                      <span class="text-xs font-semibold text-slate-900 dark:text-[#F1F5F9]">
                        {{ formatDate(entry.date) }}
                      </span>
                    </div>
                  </td>
                  <td class="px-4 py-3.5 whitespace-nowrap">
                    <span class="font-mono text-xs font-bold text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-[#18243A] px-2 py-0.5 rounded-md inline-block">
                      {{ entry.rollNumber || "N/A" }}
                    </span>
                  </td>
                  <td class="px-4 py-3.5 whitespace-nowrap">
                    <div class="flex items-center gap-3">
                      <div
                        class="h-8 w-8 rounded-xl overflow-hidden shrink-0 border border-slate-200/90 dark:border-slate-700 flex items-center justify-center bg-slate-100 dark:bg-[#18243A] shadow-xs"
                      >
                        <!-- Show photo if available -->
                        <img
                          v-if="entry.studentPhoto"
                          :src="entry.studentPhoto"
                          alt="Student Photo"
                          class="h-full w-full object-cover"
                        />

                        <!-- Fallback to initials -->
                        <div
                          v-else
                          class="h-full w-full bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center text-white font-bold text-xs"
                        >
                          {{ getInitials(entry.studentName) }}
                        </div>
                      </div>

                      <span class="text-xs sm:text-sm font-bold text-slate-900 dark:text-[#F1F5F9]">
                        {{ entry.studentName || "N/A" }}
                      </span>
                    </div>
                  </td>
                  <td class="px-4 py-3.5 whitespace-nowrap">
                    <span
                      :class="getStatusClass(entry.status)"
                      class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold rounded-full border shadow-xs"
                    >
                      <div
                        class="w-1.5 h-1.5 rounded-full"
                        :class="getStatusDotClass(entry.status)"
                      ></div>
                      {{ capitalizeStatus(entry.status) }}
                    </span>
                  </td>
                  <td class="px-4 py-3.5 whitespace-nowrap">
                    <div class="flex items-center gap-2">
                      <User class="w-3.5 h-3.5 text-slate-400 dark:text-[#7E8DA5]" />
                      <span class="text-xs font-medium text-slate-600 dark:text-[#A6B4C8]">
                        {{ entry.teacherName || entry.device?.deviceName || "N/A" }}
                      </span>
                    </div>
                  </td>
                  <td class="px-4 py-3.5 whitespace-nowrap">
                    <div class="flex items-center gap-2">
                      <Clock class="w-3.5 h-3.5 text-slate-400 dark:text-[#7E8DA5]" />
                      <span class="text-xs font-medium text-slate-500 dark:text-[#A6B4C8]">
                        {{ formatTime(entry.updatedAt || entry.createdAt) }}
                      </span>
                    </div>
                  </td>
                </tr>
              </tbody>

              <!-- Date Range View Body -->
              <tbody
                v-else
                class="divide-y divide-slate-200/70 dark:divide-[#263449] bg-white dark:bg-[#111C30]"
              >
                <tr
                  v-for="student in groupedRangeData"
                  :key="student.studentId"
                  class="hover:bg-slate-50/60 dark:hover:bg-[#18243A]/50 transition-colors"
                >
                  <td class="px-4 py-3.5 whitespace-nowrap">
                    <span class="font-mono text-xs font-bold text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-[#18243A] px-2 py-0.5 rounded-md inline-block">
                      {{ student.rollNo || "N/A" }}
                    </span>
                  </td>
                  <td class="px-4 py-3.5 whitespace-nowrap">
                    <div class="flex items-center gap-3">
                      <div
                        class="h-8 w-8 rounded-xl overflow-hidden shrink-0 border border-slate-200/90 dark:border-slate-700 flex items-center justify-center bg-slate-100 dark:bg-[#18243A] shadow-xs"
                      >
                        <!-- Show photo if available -->
                        <img
                          v-if="student.studentPhoto"
                          :src="student.studentPhoto"
                          alt="Student Photo"
                          class="h-full w-full object-cover"
                        />

                        <!-- Fallback to initials -->
                        <div
                          v-else
                          class="h-full w-full bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center text-white font-bold text-xs"
                        >
                          {{ getInitials(student.studentName) }}
                        </div>
                      </div>

                      <span class="text-xs sm:text-sm font-bold text-slate-900 dark:text-[#F1F5F9]">
                        {{ student.studentName || "N/A" }}
                      </span>
                    </div>
                  </td>
                  <td
                    v-for="date in dateRangeArray"
                    :key="date"
                    class="px-2 py-3.5 text-center whitespace-nowrap"
                  >
                    <span
                      v-if="student.attendance[date]"
                      :class="getStatusClass(student.attendance[date].status)"
                      class="inline-flex items-center justify-center w-7 h-7 text-xs font-bold rounded-lg border shadow-xs"
                    >
                      {{ getStatusIcon(student.attendance[date].status) }}
                    </span>
                    <span
                      v-else
                      class="text-slate-300 dark:text-slate-600 text-xs"
                    >-</span>
                  </td>
                  <td class="px-4 py-3.5 whitespace-nowrap text-center">
                    <span
                      class="text-xs font-bold truncate max-w-[150px]"
                      :class="{
                        'text-emerald-600 dark:text-emerald-400':
                          student.overallStatus === 'Present',
                        'text-rose-600 dark:text-rose-400':
                          student.overallStatus === 'Absent',
                        'text-slate-700 dark:text-[#A6B4C8]':
                          student.overallStatus !== 'Present' &&
                          student.overallStatus !== 'Absent',
                      }"
                      v-html="student.overallStatus"
                      :title="student.overallStatus.replace(/<[^>]+>/g, '')"
                    >
                    </span>
                  </td>

                  <td class="px-4 py-3.5 whitespace-nowrap">
                    <span class="text-xs font-medium text-slate-600 dark:text-[#A6B4C8]">
                      {{
                        getStudentDeviceName(student) ||
                        student.lastTeacher ||
                        "N/A"
                      }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Desktop Empty State -->
            <div
              v-if="!history || history.length === 0"
              class="text-center py-16"
            >
              <div
                class="w-14 h-14 bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-900/30 rounded-2xl flex items-center justify-center mx-auto mb-3.5 shadow-xs"
              >
                <ClipboardList class="w-7 h-7" />
              </div>
              <h3 class="text-base font-bold text-slate-900 dark:text-[#F1F5F9] mb-1">
                No attendance records found
              </h3>
              <p class="text-xs text-slate-500 dark:text-[#A6B4C8] max-w-sm mx-auto">
                Try adjusting your filters, classroom selection, or check back later.
              </p>
            </div>
          </div>

          <!-- Pagination Section - Mobile Responsive (only for single day view) -->
          <div
            v-if="dateRangeOption === 'today' && !isDateRangeLoading"
            class="p-3.5 sm:p-4 border-t border-slate-200/80 dark:border-[#263449] bg-slate-50/50 dark:bg-[#0F172A]/30 flex flex-col sm:flex-row items-center justify-between gap-3"
          >
            <!-- Results summary -->
            <div
              class="text-xs text-slate-500 dark:text-[#A6B4C8] font-medium text-center sm:text-left"
            >
              <template v-if="!isTableLoading">
                Showing
                <span class="font-bold text-slate-900 dark:text-[#F1F5F9]">
                  {{ (currentPage - 1) * limit + 1 }}
                </span>
                to
                <span class="font-bold text-slate-900 dark:text-[#F1F5F9]">
                  {{ Math.min(currentPage * limit, totalResults) }}
                </span>
                of
                <span class="font-bold text-slate-900 dark:text-[#F1F5F9]">
                  {{ totalResults }}
                </span>
                results
              </template>
              <template v-else>
                <div class="flex items-center gap-2 justify-center sm:justify-start">
                  <AppLoader variant="button" class="text-blue-600 dark:text-cyan-400" />
                  <span class="text-xs text-slate-500 dark:text-slate-400 font-medium">Loading results...</span>
                </div>
              </template>
            </div>

            <!-- Pagination buttons -->
            <div class="flex items-center gap-1.5 flex-wrap justify-center">
              <!-- Previous button -->
              <button
                @click="goToPage(currentPage - 1)"
                :disabled="currentPage === 1 || isTableLoading"
                class="w-8 h-8 rounded-xl flex items-center justify-center border border-slate-200 dark:border-[#263449] bg-white dark:bg-[#111C30] text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[#18243A] disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-xs"
              >
                <ChevronLeft class="w-4 h-4" />
              </button>

              <!-- Numbered buttons -->
              <template
                v-for="page in totalPages"
                :key="page"
              >
                <button
                  @click="goToPage(page)"
                  :disabled="isTableLoading"
                  :class="[
                    'min-w-[32px] h-8 px-2 rounded-xl text-xs font-bold transition-all shadow-xs flex items-center justify-center',
                    page === currentPage
                      ? 'bg-blue-600 text-white shadow-blue-500/20'
                      : 'border border-slate-200 dark:border-[#263449] bg-white dark:bg-[#111C30] text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[#18243A]',
                    'disabled:opacity-40 disabled:cursor-not-allowed',
                  ]"
                >
                  {{ page }}
                </button>
              </template>

              <!-- Next button -->
              <button
                @click="goToPage(currentPage + 1)"
                :disabled="currentPage === totalPages || isTableLoading"
                class="w-8 h-8 rounded-xl flex items-center justify-center border border-slate-200 dark:border-[#263449] bg-white dark:bg-[#111C30] text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[#18243A] disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-xs"
              >
                <ChevronRight class="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
</template>

<script setup>
  import { ref, computed, onMounted, watch, nextTick } from "vue";
  import { useStore } from "vuex";
  import {
    Search,
    Calendar,
    ChevronDown,
    UserCheck,
    UserX,
    Clock,
    TrendingUp,
    User,
    ClipboardList,
    ChevronLeft,
    ChevronRight,
    Users,
    History,
    Download,
    SlidersHorizontal,
    Filter,
    RefreshCw,
    CheckCircle2,
    XCircle,
    AlertCircle,
    Hash,
    Laptop,
  } from "lucide-vue-next";

  const store = useStore();

  // Loading States - Enhanced with date range loading
  const isInitialLoading = ref(false);
  const isLoading = ref(false);
  const isStatsLoading = ref(false);
  const isTableLoading = ref(false);
  const isSearching = ref(false);
  const isDateRangeLoading = ref(false); // New loading state for date range changes
  const loadingMessage = ref("Loading dashboard...");
  const tableLoadingMessage = ref("Loading attendance records...");
  const dateRangeLoadingMessage = ref("Loading date range data..."); // New loading message

  // Mobile state
  const showMobileFilters = ref(true);

  // Form filter fields
  const searchInput = ref(null);
  const search = ref("");
  const selectedClass = ref("");
  const selectedDate = ref(new Date().toISOString().split("T")[0]);

  // New date range option selector
  const dateRangeOption = ref("today");
  const startDate = ref(new Date().toISOString().split("T")[0]);
  const endDate = ref(new Date().toISOString().split("T")[0]);

  const isMineOnly = ref(false);
  const currentPage = ref(1);

  // Getters from Vuex
  const history = computed(() => store.getters.getAttendanceHistory);
  const stats = computed(() => store.getters.getAttendanceStats);
  const pagination = computed(() => store.getters.getAttendancePagination);
  const totalPages = computed(() => pagination.value.pages ?? 0);
  const totalResults = computed(() => pagination.value.total || 0);
  const limit = computed(() => pagination.value.limit || 20);
  const availableClasses = computed(() => store.getters.allClasses || []);

  // Computed for showing Today / Yesterday / Date
  const displayDate = computed(() => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const todayStr = today.toISOString().split("T")[0];
    const yesterdayStr = yesterday.toISOString().split("T")[0];

    if (selectedDate.value === todayStr) {
      return "Today";
    } else if (selectedDate.value === yesterdayStr) {
      return "Yesterday";
    } else {
      return formatDate(selectedDate.value);
    }
  });

  const presentCount = computed(() => {
    return history.value.filter((record) => record.status === "present").length;
  });

  const absentCount = computed(() => {
    return history.value.filter((record) => record.status === "absent").length;
  });

  const dateRangeArray = computed(() => {
    if (dateRangeOption.value === "today" || !startDate.value || !endDate.value)
      return [];

    const dates = [];
    const start = new Date(startDate.value);
    const end = new Date(endDate.value);

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      dates.push(new Date(d).toISOString().split("T")[0]);
    }

    return dates;
  });

  const groupedRangeData = computed(() => {
    if (dateRangeOption.value === "today" || !history.value.length) return [];

    const studentMap = new Map();

    history.value.forEach((record) => {
      const studentId = record.studentId || record._id;
      const dateKey = new Date(record.date).toISOString().split("T")[0];

      if (!studentMap.has(studentId)) {
        studentMap.set(studentId, {
          studentId,
          studentName: record.studentName,
          rollNo: record.rollNumber,
          studentPhoto: record.studentPhoto || record.photo || null,
          attendance: {},
          lastTeacher: record.teacherName,
          lastTime: record.updatedAt || record.createdAt,

          overallStatus: "Mixed",
        });
      }

      const student = studentMap.get(studentId);
      student.attendance[dateKey] = {
        status: record.status,
        teacher: record.teacherName,
        deviceName: record.device?.deviceName || null,
        time: formatTime(record.createdAt),
      };

      const recordTime = record.updatedAt || record.createdAt;

      if (
        !student.lastTime ||
        new Date(recordTime) > new Date(student.lastTime)
      ) {
        student.lastTeacher = record.teacherName;
        student.lastTime = recordTime;
      }
    });

    // Calculate overall status for each student
    studentMap.forEach((student) => {
      const attendanceRecords = Object.values(student.attendance);
      const presentCount = attendanceRecords.filter(
        (a) => a.status === "present",
      ).length;
      const totalDays = dateRangeArray.value.length;

      if (presentCount === totalDays) {
        student.overallStatus = "Present";
      } else if (presentCount === 0) {
        student.overallStatus = "Absent";
      } else {
        student.overallStatus = `${presentCount} day${
          presentCount > 1 ? "s" : ""
        } (<span class="text-green-600 font-bold">present</span>) out of ${totalDays} day${
          totalDays > 1 ? "s" : ""
        }`;
      }
    });

    return Array.from(studentMap.values()).sort((a, b) =>
      compareRollNumbers(a.rollNo, b.rollNo),
    );
  });

  const sortedHistory = computed(() => {
    if (!history.value) return [];
    return [...history.value].sort((a, b) =>
      compareRollNumbers(a.rollNumber, b.rollNumber),
    );
  });

  // CSV Export Function
  const exportToCSV = () => {
    if (!history.value || history.value.length === 0) {
      alert("No data available to export");
      return;
    }

    let csvContent = "";
    let filename = "";

    if (dateRangeOption.value === "today") {
      // Single day export
      csvContent = "Date,Roll Number,Student Name,Status,Teacher,Time\n";

      sortedHistory.value.forEach((entry) => {
        const row = [
          formatDate(entry.date),
          entry.rollNumber || "N/A",
          `"${entry.studentName || "N/A"}"`,
          capitalizeStatus(entry.status),
          `"${entry.teacherName || "N/A"}"`,
          formatTime(entry.createdAt),
        ].join(",");
        csvContent += row + "\n";
      });

      filename = `attendance_${selectedDate.value}.csv`;
    } else {
      // Date range export
      const headers = [
        "Roll Number",
        "Student Name",
        ...dateRangeArray.value,
        "Overall Status",
        "Last Teacher",
      ];
      csvContent = headers.join(",") + "\n";

      groupedRangeData.value.forEach((student) => {
        const row = [
          student.rollNo || "N/A",
          `"${student.studentName}"`,
          ...dateRangeArray.value.map((date) => {
            const attendance = student.attendance[date];
            return attendance ? getStatusIcon(attendance.status) : "-";
          }),
          `"${student.overallStatus.replace(/<[^>]+>/g, "")}"`,
          `"${student.lastTeacher || "N/A"}"`,
        ];
        csvContent += row.join(",") + "\n";
      });

      filename = `attendance_${startDate.value}_to_${endDate.value}.csv`;
    }

    // Create and download the file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Methods
  const getInitials = (name) => {
    if (!name || typeof name !== "string") return "NA";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "present":
        return "bg-green-100 dark:bg-emerald-950/60 text-green-800 dark:text-emerald-300 border border-green-200 dark:border-emerald-800/40";
      case "absent":
        return "bg-red-100 dark:bg-rose-950/60 text-red-800 dark:text-rose-300 border border-red-200 dark:border-rose-800/40";
      case "late":
        return "bg-yellow-100 dark:bg-amber-950/60 text-yellow-800 dark:text-amber-300 border border-yellow-200 dark:border-amber-800/40";
      default:
        return "bg-gray-100 dark:bg-[#18243A] text-gray-800 dark:text-[#A6B4C8] border border-gray-200 dark:border-[#263449]";
    }
  };

  const getStatusDotClass = (status) => {
    switch (status) {
      case "present":
        return "bg-green-500";
      case "absent":
        return "bg-red-500";
      case "late":
        return "bg-yellow-500";
      default:
        return "bg-gray-500";
    }
  };

  const capitalizeStatus = (status) => {
    return status ? status.charAt(0).toUpperCase() + status.slice(1) : "N/A";
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getAttendancePercentage = () => {
    const classKey = String(selectedClass.value);
    const classStats = stats.value?.today?.classWise?.[classKey];
    if (classStats && classStats.totalStudents > 0) {
      return Math.round((classStats.present / classStats.totalStudents) * 100);
    }
    const overall = stats.value?.today?.overall;
    if (!overall || overall.totalStudents === 0) return 0;
    return Math.round((overall.totalPresent / overall.totalStudents) * 100);
  };

  const getWeeklyAverage = () => {
    const daily = stats.value?.daily;
    if (!daily || daily.length === 0) return 0;
    const totalDays = daily.slice(0, 7);
    const avgPercentage =
      totalDays.reduce((acc, day) => {
        const total = day.totalPresent + day.totalAbsent;
        return acc + (total > 0 ? (day.totalPresent / total) * 100 : 0);
      }, 0) / totalDays.length;
    return Math.round(avgPercentage);
  };

  const formatDateHeader = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "present":
        return "P";
      case "absent":
        return "A";
      case "late":
        return "L";
      default:
        return "?";
    }
  };

  const formatDateRange = (start, end) => {
    if (!start || !end) return "";
    const startFormatted = new Date(start).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    const endFormatted = new Date(end).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    return start === end
      ? startFormatted
      : `${startFormatted} - ${endFormatted}`;
  };

  // Enhanced handle date range change with proper loading states
  const handleDateRangeChange = async () => {
    // Set loading state immediately
    isDateRangeLoading.value = true;
    dateRangeLoadingMessage.value = "Updating date range...";

    const today = new Date().toISOString().split("T")[0];

    switch (dateRangeOption.value) {
      case "today": {
        selectedDate.value = today;
        startDate.value = today;
        endDate.value = today;
        dateRangeLoadingMessage.value = "Loading today's records...";
        break;
      }
      case "yesterday": {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yDate = yesterday.toISOString().split("T")[0];
        selectedDate.value = yDate;
        startDate.value = yDate;
        endDate.value = yDate;
        dateRangeLoadingMessage.value = "Loading yesterday's records...";
        break;
      }
      case "last7days": {
        const last7Days = new Date();
        last7Days.setDate(last7Days.getDate() - 6);
        startDate.value = last7Days.toISOString().split("T")[0];
        endDate.value = today;
        dateRangeLoadingMessage.value = "Loading last 7 days...";
        break;
      }
      case "last30days": {
        const last30Days = new Date();
        last30Days.setDate(last30Days.getDate() - 29);
        startDate.value = last30Days.toISOString().split("T")[0];
        endDate.value = today;
        dateRangeLoadingMessage.value = "Loading last 30 days...";
        break;
      }
      case "custom": {
        dateRangeLoadingMessage.value = "Loading custom date range...";
        break;
      }
    }

    currentPage.value = 1;

    try {
      // Fetch data with proper loading states
      await fetchFilteredHistory();

      if (dateRangeOption.value === "today") {
        await fetchStats();
      }
    } catch (error) {
      console.error("Error handling date range change:", error);
    } finally {
      // Small delay to prevent flashing
      setTimeout(() => {
        isDateRangeLoading.value = false;
      }, 300);
    }
  };

  // Fetch attendance and stats on mount
  onMounted(async () => {
    isInitialLoading.value = true;
    loadingMessage.value = "Initializing dashboard...";
    try {
      await store.dispatch("fetchAllClasses");
      await Promise.all([fetchStats(), fetchFilteredHistory()]);
    } finally {
      isInitialLoading.value = false;
    }
  });

  // Watch filters with debouncing for search
  let searchTimeout;
  watch(search, async () => {
    clearTimeout(searchTimeout);
    isSearching.value = true;
    searchTimeout = setTimeout(async () => {
      currentPage.value = 1;
      await fetchFilteredHistory();
      await nextTick();
      if (searchInput.value) {
        searchInput.value.focus();
      }
    }, 300);
  });

  watch([selectedClass, selectedDate, isMineOnly, startDate, endDate], () => {
    // Don't trigger if date range is already loading
    if (!isDateRangeLoading.value) {
      currentPage.value = 1;
      fetchFilteredHistory();
      if (dateRangeOption.value === "today") {
        fetchStats();
      }
    }
  });

  watch(
    () => pagination.value.page,
    (val) => {
      currentPage.value = val;
    },
  );

  // Fetch stats with filters
  async function fetchStats() {
    isStatsLoading.value = true;
    try {
      const filters = {};
      if (selectedClass.value) {
        filters.classFilter = selectedClass.value;
      }
      if (dateRangeOption.value === "today" && selectedDate.value) {
        const formattedDate =
          typeof selectedDate.value === "string"
            ? selectedDate.value
            : new Date(selectedDate.value).toISOString().split("T")[0];
        filters.date = formattedDate;
      }
      await store.dispatch("fetchAttendanceStats", filters);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      isStatsLoading.value = false;
    }
  }

  // Enhanced fetch data with better loading state management
  async function fetchFilteredHistory() {
    const isInitialLoad = currentPage.value === 1 && history.value.length === 0;

    // Don't set table loading if date range is already loading
    if (!isDateRangeLoading.value) {
      if (isInitialLoad && !isInitialLoading.value) {
        isTableLoading.value = true;
        tableLoadingMessage.value = "Loading attendance records...";
      } else if (!isInitialLoading.value) {
        isTableLoading.value = true;
        tableLoadingMessage.value =
          currentPage.value === 1
            ? "Filtering records..."
            : `Loading page ${currentPage.value}...`;
      }
    }

    try {
      const filters = {
        page: currentPage.value,
        search: search.value || "",
        class: selectedClass.value || "",
      };

      // Apply limit only if single day view
      if (dateRangeOption.value === "today") {
        filters.limit = limit.value;
      }

      // Handle date filtering based on mode
      if (dateRangeOption.value !== "today") {
        if (startDate.value) filters.startDate = startDate.value;
        if (endDate.value) filters.endDate = endDate.value;
      } else {
        if (selectedDate.value) {
          filters.startDate = selectedDate.value;
          filters.endDate = selectedDate.value;
        }
      }

      if (isMineOnly.value) {
        filters.self = true;
      }

      await store.dispatch("fetchAttendanceHistory", filters);
    } catch (error) {
      console.error("Error fetching attendance history:", error);
    } finally {
      if (!isDateRangeLoading.value) {
        isTableLoading.value = false;
      }
      isSearching.value = false;
      isLoading.value = false;
    }
  }

  // Pagination navigation
  function goToPage(page) {
    if (
      page >= 1 &&
      page <= totalPages.value &&
      !isTableLoading.value &&
      !isDateRangeLoading.value
    ) {
      currentPage.value = page;
      isTableLoading.value = true;
      tableLoadingMessage.value = `Loading page ${page}...`;
      fetchFilteredHistory();
    }
  }
  function getStudentDeviceName(student) {
    if (!student.attendance) return null;

    for (const key in student.attendance) {
      const rec = student.attendance[key];
      if (rec?.deviceName) {
        return rec.deviceName;
      }
    }
    return null;
  }
  function compareRollNumbers(a, b) {
    if (!a) return 1;
    if (!b) return -1;

    const regex = /^([a-zA-Z]*)([-\s]*)(\d+)$/;

    const matchA = a.toString().trim().match(regex);
    const matchB = b.toString().trim().match(regex);

    // If both match pattern like D-1001
    if (matchA && matchB) {
      const prefixA = matchA[1];
      const prefixB = matchB[1];

      const numA = parseInt(matchA[3], 10);
      const numB = parseInt(matchB[3], 10);

      // First sort by prefix (D, A, B...)
      if (prefixA !== prefixB) {
        return prefixA.localeCompare(prefixB);
      }

      // Then by number
      return numA - numB;
    }

    // fallback natural string sort
    return a.localeCompare(b, undefined, {
      numeric: true,
      sensitivity: "base",
    });
  }
</script>

<style scoped>
  /* Custom animations */
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  .animate-spin {
    animation: spin 1s linear infinite;
  }
  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
  .animate-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }
  /* Custom scrollbar */
  .overflow-x-auto::-webkit-scrollbar {
    height: 6px;
  }
  .overflow-x-auto::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  .overflow-x-auto::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  .overflow-x-auto::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
  html.dark .overflow-x-auto::-webkit-scrollbar-track {
    background: #0b1120;
  }
  html.dark .overflow-x-auto::-webkit-scrollbar-thumb {
    background: #263449;
  }
  /* Custom select arrow */
  select {
    background-image: none;
  }
  /* Disabled state improvements */
  .disabled\:opacity-50:disabled {
    opacity: 0.5;
  }
  .disabled\:cursor-not-allowed:disabled {
    cursor: not-allowed;
  }
  /* Mobile-specific optimizations */
  @media (max-width: 640px) {
    /* Ensure minimum touch targets */
    button {
      min-height: 44px;
      min-width: 44px;
    }
    input,
    select {
      min-height: 44px;
    }
    /* Optimize text sizes for mobile */
    .text-xs {
      font-size: 0.75rem;
      line-height: 1rem;
    }
    .text-sm {
      font-size: 0.875rem;
      line-height: 1.25rem;
    }
  }
  /* Tablet optimizations */
  @media (min-width: 641px) and (max-width: 1023px) {
    /* Adjust spacing for tablet */
    .p-4 {
      padding: 1.5rem;
    }
    .gap-3 {
      gap: 0.75rem;
    }
  }
  /* Accessibility improvements */
  @media (prefers-reduced-motion: reduce) {
    * {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }
</style>
